import React, { useRef, useEffect } from 'react'
import { css, cx } from '@emotion/css'
import { TextInput } from '../../../../legacy-design-system/form-text'
import { TextInputProps as TextInputProperties } from '../../../../legacy-design-system/form-text/text-input'
import { theme } from '@contrast-security-inc/design-system-foundations'

const dropdownCss = css`
  border: 1px solid ${theme.color.neutral50};
  background: ${theme.color.white};
  font-family: ${theme.font.body.font};
  font-size: 14px;
  transition: background-color;
  list-style-type: none;
  text-align: left;
  margin: 0;
  padding: 0;
  position: absolute;
  z-index: 1000;
  width: 100%;
`

const listItemCss = css`
  padding: 7px 10px;
  cursor: pointer;
  &:hover {
    background: #e7f4f2;
  }
`

const disabledListItemCss = css`
  color: ${theme.color.neutral50};
  cursor: not-allowed;
`

export interface TypeAheadDropdownProps {
  TextInputProps: TextInputProperties // TextInputProps
  hidden: boolean
  values: any[]
  disableUnassignedAndDefault?: boolean
  onSelectionClicked: (any) => void
  onOutsideActivity: () => void
  itemToString: (any) => string
  itemToKey: (any) => string
  testId?: string
}

export const TypeAheadDropdown = ({
  TextInputProps,
  hidden,
  values = [],
  disableUnassignedAndDefault = false,
  onSelectionClicked,
  onOutsideActivity,
  itemToString,
  itemToKey,
  testId
}: TypeAheadDropdownProps) => {
  const dropdownMenu = useRef<HTMLUListElement>(null)
  const userField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    function handleOutsideClick(event) {
      if (
        dropdownMenu &&
        dropdownMenu.current &&
        !dropdownMenu.current.contains(event.target)
      ) {
        onOutsideActivity()
      }
    }

    function handleOutsideFocus() {
      if (userField.current !== document.activeElement) {
        onOutsideActivity()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keyup', handleOutsideFocus)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keyup', handleOutsideFocus)
    }
  }, [onOutsideActivity])

  return (
    <div className={css(`position: relative;`)}>
      <TextInput
        {...TextInputProps}
        inputRef={userField}
        data-testid={`type-ahead-dropdown-input-${testId}`}
      />
      {!hidden && (
        <ul
          ref={dropdownMenu}
          className={dropdownCss}
          data-testid={`type-ahead-dropdown-ul-${testId}`}
        >
          {values.map((value) => {
            // eslint-disable-next-line unicorn/prefer-ternary
            if (
              disableUnassignedAndDefault &&
              (value.accountId === '' || value.accountId === '-1')
            ) {
              return (
                <li
                  data-testid={`disabled-type-ahead-dropdown-suggestion-${itemToKey(
                    value
                  )}`}
                  key={`disabled-suggestion-${itemToKey(value)}`}
                  value={itemToString(value)}
                  className={cx(listItemCss, disabledListItemCss)}
                >
                  {itemToString(value)}
                </li>
              )
            } else {
              return (
                <li
                  data-testid={`type-ahead-dropdown-suggestion-${itemToKey(
                    value
                  )}`}
                  key={`suggestion-${itemToKey(value)}`}
                  onClick={() => onSelectionClicked(value)}
                  value={itemToString(value)}
                  className={listItemCss}
                >
                  {itemToString(value)}
                </li>
              )
            }
          })}
        </ul>
      )}
    </div>
  )
}
